import { useState } from 'react';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type { ErrorReasonEntry } from '@infras/shared-types';
import { formatDate } from '../lib/format';

/**
 * The help-centre URL overlay — shared by the dictionary list and the code detail
 * page so the two can't drift on what "has an article" means.
 *
 * It is the ONE editable thing in the dictionary. Everything else (copy, reason,
 * taxonomy) is a reviewed contract in shared-types; a doc_url is written after the
 * code ships, by whoever closes the gap, and changes again whenever the help centre
 * is reorganised. So it lives in Postgres (migration 045), keyed by the stable
 * customer-facing CODE, and overlays the static entry.
 */
export const ERROR_CODE_DOCS = gql`
  query ErrorCodeDocs {
    errorCodeDocs {
      code
      docUrl
      setBy
      setAt
    }
  }
`;

export const SET_ERROR_CODE_DOC = gql`
  mutation SetErrorCodeDoc($code: String!, $docUrl: String!) {
    setErrorCodeDoc(code: $code, docUrl: $docUrl)
  }
`;

export interface DocOverride {
  code: string;
  docUrl: string;
  setBy: string;
  setAt: string;
}

/** Curated URLs by code, plus the resolver that falls back to the static entry. */
export function useErrorCodeDocs() {
  const { data, refetch } = useQuery<{ errorCodeDocs: DocOverride[] }>(ERROR_CODE_DOCS, {
    fetchPolicy: 'cache-and-network',
  });
  // `docs` is the stable identity a caller can pass to useMemo — `overrides` is a
  // fresh Map every render and would defeat any dependency array it appears in.
  const docs = data?.errorCodeDocs;
  const overrides = new Map<string, DocOverride>();
  for (const d of data?.errorCodeDocs ?? []) overrides.set(d.code, d);
  return {
    docs,
    overrides,
    docFor: (e: ErrorReasonEntry): string | null =>
      overrides.get(e.code)?.docUrl ?? e.docUrl ?? null,
    refetchDocs: refetch,
  };
}

/**
 * Edit-in-place article link. Clearing the box DELETES the override rather than
 * saving '', so the code falls back to the file and the "no article" count stays
 * true — an empty string would read as "documented" everywhere the URL is only
 * tested for presence.
 */
export function ArticleField({
  code,
  docUrl,
  override,
  canEdit,
  onSaved,
  /** `link` = one inline line for a list row; `block` = stacked, for the rail. */
  variant = 'link',
}: {
  code: string;
  docUrl: string | null;
  override?: DocOverride;
  canEdit: boolean;
  onSaved: () => void;
  variant?: 'link' | 'block';
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(docUrl ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [save, { loading }] = useMutation(SET_ERROR_CODE_DOC, {
    onCompleted: () => {
      setEditing(false);
      setErr(null);
      onSaved();
    },
    onError: (e) => setErr(e.message),
  });

  if (!editing) {
    return (
      <div className={`ec-article ec-article--${variant}`}>
        {docUrl ? (
          <a href={docUrl} target="_blank" rel="noreferrer">
            {variant === 'block' ? docUrl : 'Help-centre article'}
          </a>
        ) : (
          // A missing article is a real gap in the dictionary, so it is stated
          // rather than left blank — but it is not an error, so it stays faint.
          <span className="ec-article__gap">No help-centre article</span>
        )}
        {canEdit && (
          <button
            className="btn btn-sm"
            onClick={() => {
              setDraft(docUrl ?? '');
              setEditing(true);
            }}
          >
            {docUrl ? 'Edit' : 'Add'}
          </button>
        )}
        {override && (
          <span className="muted text-xs" title={`set by ${override.setBy}`}>
            {formatDate(override.setAt)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`ec-article ec-article--${variant}`}>
      <input
        className="kv-edit__input"
        type="url"
        autoFocus
        value={draft}
        placeholder="https://support.optisigns.com/hc/en-us/articles/…"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save({ variables: { code, docUrl: draft } });
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <div className="btn-row ec-article__actions">
        <button
          className="btn btn-sm btn-primary"
          disabled={loading}
          onClick={() => void save({ variables: { code, docUrl: draft } })}
        >
          {loading ? 'Saving…' : 'Save'}
        </button>
        <button className="btn btn-sm btn-ghost" disabled={loading} onClick={() => setEditing(false)}>
          Cancel
        </button>
        <span className="muted text-xs">Empty removes the link.</span>
      </div>
      {err && <p className="error text-xs">{err}</p>}
    </div>
  );
}
